import React, { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { QuestionData, Answer } from "../../../context/questionnaire/types";
import { isHeightQuestion, isWeightQuestion } from "../../../helpers/questionnaire-helpers";
import ChoiceRenderer from "./choice";
import ChoiceMultiRenderer from "./choice-multi";
import ChoiceMultiDropdownRenderer from "./choice-multi-dropdown";
import TextInputRenderer from "./text-input";
import ParagraphInputRenderer from "./paragraph-input";
import SelectInputRenderer from "./select-input";
import NumberInputRenderer from "./number-input";
import DateInputRenderer from "./date-input";
import PhoneInputRenderer from "./phone-input";
import EmailInputRenderer from "./email-input";
import DisplayTextRenderer from "./display-text";
import ContactInfoRenderer from "./contact-info";
import MultiCheckboxRenderer from "./multi-checkbox";
import HeightSelectRenderer from "./height-select";
import ColumnsSelectNumberRenderer from "./columns-select-number";
import MultiColumnNestedRenderer from "./multi-column-nested";
import FamilyHistoryRenderer from "./family-history";
import WeightInputRenderer from "./weight-input";
import VideoRenderer from "./video";

export interface RendererProps {
  question: QuestionData;
  answer: Answer | undefined;
  onAnswer: (value: any, isValid: boolean, values?: any[], autoAdvanceReady?: boolean) => void;
}

const rendererMap: Record<string, React.FC<RendererProps>> = {
  choice: ChoiceRenderer,
  choice_multi: ChoiceMultiRenderer,
  choice_multi_dropdown: ChoiceMultiDropdownRenderer,
  text_input: TextInputRenderer,
  short_answer: TextInputRenderer,
  paragraph_input: ParagraphInputRenderer,
  select: SelectInputRenderer,
  number: NumberInputRenderer,
  date_input: DateInputRenderer,
  month_date_input: DateInputRenderer,
  future_date: DateInputRenderer,
  phone_number: PhoneInputRenderer,
  email_input: EmailInputRenderer,
  email: EmailInputRenderer,
  email_confirmation: EmailInputRenderer,
  display_text: DisplayTextRenderer,
  extended_display_text: DisplayTextRenderer,
  contact_info: ContactInfoRenderer,
  multi_checkbox: MultiCheckboxRenderer,
  columns_select_number: ColumnsSelectNumberRenderer,
  multi_column_nested: MultiColumnNestedRenderer,
  multi_column_nested_expansion: FamilyHistoryRenderer,
  video: VideoRenderer,
};

export function isSupportedQuestion(question: QuestionData): boolean {
  return isHeightQuestion(question) || isWeightQuestion(question) || question.type in rendererMap;
}

const UnsupportedFallback: React.FC<{ question: QuestionData }> = ({
  question,
}) => {
  const sentRef = useRef(false);
  useEffect(() => {
    if (!sentRef.current) {
      sentRef.current = true;
      Sentry.captureMessage(
        `Unsupported question type: ${question.type}`,
        {
          extra: { questionType: question.type, questionName: question.name },
        }
      );
    }
  }, [question.type, question.name]);
  return null;
};

const QuestionRenderer: React.FC<RendererProps> = (props) => {
  if (isHeightQuestion(props.question)) {
    return <HeightSelectRenderer {...props} />;
  }
  if (isWeightQuestion(props.question)) {
    return <WeightInputRenderer {...props} />;
  }

  const Renderer = rendererMap[props.question.type];
  if (!Renderer) {
    return <UnsupportedFallback question={props.question} />;
  }
  return <Renderer {...props} />;
};

export default QuestionRenderer;
